/**
 * Data Service Module
 * 
 * Single Responsibility Principle: This service only handles data management
 * Provides access to track and car class information for the application
 * 
 * Following SOLID principles:
 * - Single Responsibility: Only manages static data access
 * - Open/Closed: Easy to extend with new tracks/cars without modification
 * - Dependency Inversion: Provides interfaces for data access
 */

import { Track, CarClass } from "../types/strategy";

/**
 * Interface for data access operations
 * 
 * Responsibility: Defines the contract for data access operations
 * Used by: DataService implementation
 */
export interface IDataService {
  /**
   * Get all available tracks
   * @returns Array of all tracks
   */
  getAllTracks(): Track[];
  
  /**
   * Find a track by its unique identifier
   * @param id - Track identifier
   * @returns Track object or undefined if not found
   */
  getTrackById(id: string): Track | undefined;
  
  /**
   * Get all available car classes
   * @returns Array of all car classes
   */
  getAllCarClasses(): CarClass[];
  
  /**
   * Find a car class by its unique identifier
   * @param id - Car class identifier
   * @returns Car class object or undefined if not found
   */
  getCarClassById(id: string): CarClass | undefined;
  
  /**
   * Get car classes filtered by category
   * @param category - Car category (GT3, LMDh, LMP2, GTE)
   * @returns Array of car classes in the specified category
   */
  getCarClassesByCategory(category: string): CarClass[];
  
  /**
   * Get all available car categories
   * @returns Array of unique car categories
   */
  getCategories(): string[];
}

/**
 * Static interface for data access operations
 * 
 * Responsibility: Defines the contract for static data access operations
 * Used by: DataService static implementation
 */
export interface IStaticDataService {
  /**
   * Get all available tracks
   * @returns Array of all tracks
   */
  getAllTracks(): Track[];
  
  /**
   * Find a track by its unique identifier
   * @param id - Track identifier
   * @returns Track object or undefined if not found
   */
  getTrackById(id: string): Track | undefined;
  
  /**
   * Get all available car classes
   * @returns Array of all car classes
   */
  getAllCarClasses(): CarClass[];
  
  /**
   * Find a car class by its unique identifier
   * @param id - Car class identifier
   * @returns Car class object or undefined if not found
   */
  getCarClassById(id: string): CarClass | undefined;
  
  /**
   * Get car classes filtered by category
   * @param category - Car category (GT3, LMDh, LMP2, GTE)
   * @returns Array of car classes in the specified category
   */
  getCarClassesByCategory(category: string): CarClass[];
  
  /**
   * Get all available car categories
   * @returns Array of unique car categories
   */
  getCategories(): string[];
}

/**
 * Data service for managing track and car class information
 * 
 * Responsibility: Provides access to static racing data (tracks and car classes)
 * Used by: useRaceStrategy hook, UI components for data selection
 * 
 * Contains static data for popular iRacing tracks and car classes.
 * This service follows the Repository pattern for data access.
 */
export class DataService {
  // ============================================================================
  // STATIC DATA STORAGE
  // ============================================================================
  
  /**
   * Static data for available racing tracks
   * 
   * Each track includes location, length, and turn count information.
   * Data is sourced from iRacing specifications and real-world measurements.
   */
  private static readonly tracks: Track[] = [
    {
      id: "daytona",
      name: "Daytona International Speedway",
      location: "Daytona Beach, Florida",
      length: 5800,
      turns: 12
    },
    {
      id: "sebring",
      name: "Sebring International Raceway",
      location: "Sebring, Florida",
      length: 6100,
      turns: 17
    },
    {
      id: "spa",
      name: "Circuit de Spa-Francorchamps",
      location: "Stavelot, Belgium",
      length: 7004,
      turns: 20
    },
    {
      id: "le_mans",
      name: "Circuit de la Sarthe",
      location: "Le Mans, France",
      length: 13626,
      turns: 38
    },
    {
      id: "nurburgring",
      name: "Nürburgring Nordschleife",
      location: "Nürburg, Germany",
      length: 20832,
      turns: 154
    },
    {
      id: "nurburgring_gp",
      name: "Nürburgring Grand-Prix",
      location: "Nürburg, Germany",
      length: 5148,
      turns: 16
    },
    {
      id: "monza",
      name: "Autodromo Nazionale di Monza",
      location: "Monza, Italy",
      length: 5793,
      turns: 11
    },
    {
      id: "silverstone",
      name: "Silverstone Circuit",
      location: "Silverstone, England",
      length: 5891,
      turns: 18
    },
    {
      id: "road_atlanta",
      name: "Road Atlanta",
      location: "Braselton, Georgia",
      length: 4020,
      turns: 12
    },
    {
      id: "watkins_glen",
      name: "Watkins Glen International",
      location: "Watkins Glen, New York",
      length: 5552,
      turns: 11
    },
    {
      id: "virginia",
      name: "Virginia International Raceway",
      location: "Alton, Virginia",
      length: 5232,
      turns: 18
    }
  ];

  /**
   * Static data for available car classes
   * 
   * Includes GT3, LMDh, LMP2, and GTE categories with realistic specifications.
   * Data is sourced from manufacturer specifications and real-world performance data.
   */
  private static readonly carClasses: CarClass[] = [
    // GT3 Cars - Grand Touring 3 class
    {
      id: "gt3_ferrari_296",
      name: "Ferrari 296 GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.2,
      avgLapTime: 107.5
    },
    {
      id: "gt3_porsche_911",
      name: "Porsche 911 GT3 R",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.1,
      avgLapTime: 107.8
    },
    {
      id: "gt3_bmw_m4",
      name: "BMW M4 GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.3,
      avgLapTime: 108.2
    },
    {
      id: "gt3_mercedes_amg",
      name: "Mercedes-AMG GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.0,
      avgLapTime: 107.3
    },
    {
      id: "gt3_lamborghini_huracan",
      name: "Lamborghini Huracán GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.4,
      avgLapTime: 108.5
    },
    {
      id: "gt3_audi_r8",
      name: "Audi R8 LMS GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.2,
      avgLapTime: 107.9
    },
    {
      id: "gt3_mclaren_720s",
      name: "McLaren 720S GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.1,
      avgLapTime: 107.6
    },
    {
      id: "gt3_aston_martin_vantage",
      name: "Aston Martin Vantage GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.3,
      avgLapTime: 108.1
    },
    {
      id: "gt3_lexus_rc_f",
      name: "Lexus RC F GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.2,
      avgLapTime: 108.3
    },
    {
      id: "gt3_corvette_c8",
      name: "Corvette C8.R GT3",
      category: "GT3",
      fuelCapacity: 120,
      avgFuelConsumption: 3.1,
      avgLapTime: 107.7
    },
    // LMDh Cars - Le Mans Daytona hybrid class
    {
      id: "lmdh_porsche_963",
      name: "Porsche 963",
      category: "LMDh",
      fuelCapacity: 75,
      avgFuelConsumption: 2.8,
      avgLapTime: 95.2
    },
    {
      id: "lmdh_cadillac_v_series",
      name: "Cadillac V-Series.R",
      category: "LMDh",
      fuelCapacity: 75,
      avgFuelConsumption: 2.9,
      avgLapTime: 95.5
    },
    {
      id: "lmdh_bmw_m_hybrid",
      name: "BMW M Hybrid V8",
      category: "LMDh",
      fuelCapacity: 75,
      avgFuelConsumption: 2.7,
      avgLapTime: 95.1
    },
    {
      id: "lmdh_acura_arx_06",
      name: "Acura ARX-06",
      category: "LMDh",
      fuelCapacity: 75,
      avgFuelConsumption: 2.8,
      avgLapTime: 95.3
    },
    {
      id: "lmdh_lamborghini_sc63",
      name: "Lamborghini SC63",
      category: "LMDh",
      fuelCapacity: 75,
      avgFuelConsumption: 2.9,
      avgLapTime: 95.8
    },
    // LMP2 Cars - Le Mans Prototype 2 class
    {
      id: "lmp2_oreca_07",
      name: "Oreca 07",
      category: "LMP2",
      fuelCapacity: 75,
      avgFuelConsumption: 2.6,
      avgLapTime: 98.5
    },
    {
      id: "lmp2_ligier_js_p217",
      name: "Ligier JS P217",
      category: "LMP2",
      fuelCapacity: 75,
      avgFuelConsumption: 2.7,
      avgLapTime: 98.8
    },
    // GTE Cars - Grand Touring Endurance class
    {
      id: "gte_corvette_c8",
      name: "Corvette C8.R",
      category: "GTE",
      fuelCapacity: 90,
      avgFuelConsumption: 2.9,
      avgLapTime: 102.3
    },
    {
      id: "gte_porsche_911_rsr",
      name: "Porsche 911 RSR",
      category: "GTE",
      fuelCapacity: 90,
      avgFuelConsumption: 2.8,
      avgLapTime: 102.1
    },
    {
      id: "gte_ferrari_488",
      name: "Ferrari 488 GTE",
      category: "GTE",
      fuelCapacity: 90,
      avgFuelConsumption: 2.9,
      avgLapTime: 102.5
    }
  ];

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Get all available tracks
   * 
   * Returns a copy of the tracks array to prevent external modification.
   * 
   * @returns Array of all tracks
   */
  static getAllTracks(): Track[] {
    return [...this.tracks];
  }

  /**
   * Find a track by its unique identifier
   * 
   * Performs a case-sensitive search for the track ID.
   * 
   * @param id - Track identifier (e.g., "daytona", "spa")
   * @returns Track object or undefined if not found
   */
  static getTrackById(id: string): Track | undefined {
    return this.tracks.find(track => track.id === id);
  }

  /**
   * Get all available car classes
   * 
   * Returns a copy of the car classes array to prevent external modification.
   * 
   * @returns Array of all car classes
   */
  static getAllCarClasses(): CarClass[] {
    return [...this.carClasses];
  }

  /**
   * Find a car class by its unique identifier
   * 
   * Performs a case-sensitive search for the car class ID.
   * 
   * @param id - Car class identifier (e.g., "gt3_ferrari_296")
   * @returns Car class object or undefined if not found
   */
  static getCarClassById(id: string): CarClass | undefined {
    return this.carClasses.find(car => car.id === id);
  }

  /**
   * Get car classes filtered by category
   * 
   * Returns all car classes that match the specified category.
   * Category matching is case-sensitive.
   * 
   * @param category - Car category (e.g., "GT3", "LMDh", "LMP2", "GTE")
   * @returns Array of car classes in the specified category
   */
  static getCarClassesByCategory(category: string): CarClass[] {
    return this.carClasses.filter(car => car.category === category);
  }

  /**
   * Get all available car categories
   * 
   * Returns a unique list of all car categories available in the system.
   * 
   * @returns Array of unique car categories
   */
  static getCategories(): string[] {
    return [...new Set(this.carClasses.map(car => car.category))];
  }
}
